import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { BlogCategoryNav } from "@/components/blog/BlogCategoryNav";
import { BlogPostCard } from "@/components/blog/BlogPostCard";
import { ContactPageAurora } from "@/components/contact/ContactPageAurora";
import { Container } from "@/components/ui/primitives";
import { useSiteContent } from "@/content/site";
import { useApp } from "@/context/AppProviders";
import { useInfiniteBlogList } from "@/hooks/useInfiniteBlogList";
import { useReveal } from "@/hooks/useReveal";
import { usePageSeo } from "@/hooks/usePageSeo";
import { fetchBlogCategories } from "@/lib/blogApi";
import { clearPaintShell } from "@/lib/clearPaintShell";
import { blogListPath, blogPostPath, homePath } from "@/lib/routes";
import {
  breadcrumbJsonLd,
  collectionPageJsonLd,
  formatBlogPostTitle,
  formatPageTitle,
} from "@/lib/seo";
import type { BlogCategory } from "@/types/blog";

export function BlogListPage() {
  const { categorySlug } = useParams<{ categorySlug?: string }>();
  const [, setSearchParams] = useSearchParams();
  const { locale } = useApp();
  const c = useSiteContent(locale);
  const isTr = locale === "tr";
  const activeCategorySlug = categorySlug?.trim() ?? "";

  const {
    items,
    activeCategory,
    loading,
    loadingMore,
    error,
    categoryNotFound,
    hasMore,
    loadMore,
    bootstrapCategories,
  } = useInfiniteBlogList({ categorySlug: activeCategorySlug });

  const [categories, setCategories] = useState<BlogCategory[]>(bootstrapCategories ?? []);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useReveal([locale, activeCategorySlug, loading, items.length]);

  useEffect(() => {
    if (!loading) clearPaintShell();
  }, [loading]);

  useEffect(() => {
    setSearchParams(
      (prev) => {
        if (!prev.get("page")) return prev;
        const next = new URLSearchParams(prev);
        next.delete("page");
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  useEffect(() => {
    if (bootstrapCategories) {
      setCategories(bootstrapCategories);
      return;
    }

    let cancelled = false;

    async function loadCategories() {
      const result = await fetchBlogCategories();
      if (cancelled || !result.ok || !result.data) return;
      setCategories(result.data.categories);
    }

    void loadCategories();
    return () => {
      cancelled = true;
    };
  }, [bootstrapCategories]);

  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node || loading || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMore();
        }
      },
      { rootMargin: "320px 0px 320px 0px", threshold: 0 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadMore, loading, items.length]);

  const heading = activeCategory?.title ?? c.blogT;
  const description = activeCategory
    ? isTr
      ? `${activeCategory.label} kategorisindeki blog yazıları.`
      : `Blog posts in ${activeCategory.label}.`
    : c.blogS;
  const seoTitle = activeCategory ? heading : c.blogMetaTitle;
  const seoDescription = activeCategory ? description : c.blogMetaDescription;
  const canonicalPath = blogListPath(locale, {
    categorySlug: activeCategorySlug || undefined,
  });

  usePageSeo(
    useMemo(
      () => ({
        title: activeCategory ? formatBlogPostTitle(seoTitle) : formatPageTitle(seoTitle),
        description: seoDescription,
        canonicalPath,
        locale,
        jsonLd: [
          breadcrumbJsonLd([
            { name: isTr ? "Ana Sayfa" : "Home", path: homePath(locale) },
            { name: "Blog", path: blogListPath(locale) },
            ...(activeCategory
              ? [{ name: activeCategory.label, path: canonicalPath }]
              : []),
          ]),
          collectionPageJsonLd({
            name: heading,
            description: seoDescription,
            path: canonicalPath,
          }),
        ],
      }),
      [activeCategory, canonicalPath, heading, isTr, locale, seoDescription, seoTitle]
    )
  );

  if (categoryNotFound) {
    return <Navigate to={blogListPath(locale)} replace />;
  }

  return (
    <>
      <section className="relative overflow-hidden bg-[var(--bg2)] pt-[72px] pb-16 sm:pb-20 lg:pb-24">
        <ContactPageAurora />
        <Container className="relative page-hero-inner">
          <nav className="mb-5 text-sm text-[var(--text-sm)]" aria-label="Breadcrumb">
            <ol className="flex flex-wrap items-center gap-2">
              <li>
                <Link to={homePath(locale)} className="no-underline transition hover:text-[var(--text)]">
                  {isTr ? "Ana Sayfa" : "Home"}
                </Link>
              </li>
              <li aria-hidden>/</li>
              <li>
                {activeCategory ? (
                  <Link to={blogListPath(locale)} className="no-underline transition hover:text-[var(--text)]">
                    {c.nav.blog}
                  </Link>
                ) : (
                  <span className="font-medium text-[var(--text)]">{c.nav.blog}</span>
                )}
              </li>
              {activeCategory ? (
                <>
                  <li aria-hidden>/</li>
                  <li>
                    <span className="font-medium text-[var(--text)]">{activeCategory.label}</span>
                  </li>
                </>
              ) : null}
            </ol>
          </nav>

          <div className="max-w-2xl">
            <h1 className="linear-section-title text-balance">{heading}</h1>
            <p className="linear-body mt-3 max-w-lg">{description}</p>
          </div>

          <BlogCategoryNav
            locale={locale}
            categories={categories}
            activeSlug={activeCategorySlug || undefined}
            className="mt-5"
          />

          <div className="mt-6 sm:mt-7">
            {loading ? (
              <p className="fade-up py-10 text-center text-sm text-[var(--text-md)]">
                {isTr ? "Yazılar yükleniyor…" : "Loading posts…"}
              </p>
            ) : null}

            {!loading && error ? (
              <p className="fade-up rounded-xl border border-red-500/25 bg-red-500/10 px-4 py-6 text-center text-sm text-red-700 dark:text-red-300">
                {isTr
                  ? "Blog yazıları yüklenemedi. Veritabanı bağlantısını kontrol edin."
                  : "Could not load blog posts. Please check the database connection."}
              </p>
            ) : null}

            {!loading && !error && items.length === 0 ? (
              <p className="fade-up py-10 text-center text-sm text-[var(--text-md)]">
                {activeCategory
                  ? isTr
                    ? "Bu kategoride henüz yayınlanmış yazı bulunmuyor."
                    : "No published posts in this category yet."
                  : isTr
                    ? "Henüz yayınlanmış yazı bulunmuyor."
                    : "No published posts yet."}
              </p>
            ) : null}

            {!loading && !error && items.length > 0 ? (
              <>
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {items.map((post, index) => (
                    <BlogPostCard
                      key={post.id}
                      post={post}
                      href={blogPostPath(locale, post.slug)}
                      variant="grid"
                      priority={index === 0}
                    />
                  ))}
                </div>

                <div ref={loadMoreRef} className="mt-10 flex min-h-12 items-center justify-center">
                  {loadingMore ? (
                    <span className="inline-flex items-center gap-2 text-sm text-[var(--text-md)]">
                      <Loader2 className="size-4 animate-spin" aria-hidden />
                      {isTr ? "Daha fazla yazı yükleniyor…" : "Loading more posts…"}
                    </span>
                  ) : hasMore ? (
                    <span className="sr-only">
                      {isTr ? "Daha fazla yazı yüklenecek" : "More posts will load"}
                    </span>
                  ) : (
                    <p className="text-sm text-[var(--text-sm)]">
                      {isTr ? "Tüm yazılar yüklendi." : "All posts loaded."}
                    </p>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </Container>
      </section>
    </>
  );
}
